import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui';
import logo from '../theme/NeuroEdgeLogo.png';
import { ContactSales, PlansCatalog } from '../modules/billing/PlansCatalog';
import { ContactDetails } from './ContactDetails';
import { EnquiryForm } from './EnquiryForm';
import { usePlatformChat } from './usePlatformChat';
import './landing.css';

const NAV_LINKS = [
  { href: '#benefits', label: 'Benefits' },
  { href: '#how-it-works', label: 'How It Works' },
  { href: '#pricing', label: 'Pricing' },
  { href: '#about', label: 'About Us' },
  { href: '#contact', label: 'Contact' },
];

const BENEFITS = [
  {
    icon: '💬',
    title: 'Automated conversations',
    text: 'Greet, qualify, and answer customers around the clock with admin-built flows - no code required.',
  },
  {
    icon: '🧾',
    title: 'AI-powered quotations',
    text: 'A customer sends a photo, PDF, or typed list; your catalog prices it and a PDF quotation goes straight back.',
  },
  {
    icon: '📇',
    title: 'Built-in CRM & leads',
    text: 'Every WhatsApp conversation becomes a contact and a lead, tracked through your own sales pipeline.',
  },
  {
    icon: '📅',
    title: 'Appointment booking',
    text: 'Customers pick a service, date, and time themselves - synced to your real availability, no double-booking.',
  },
  {
    icon: '🌐',
    title: 'Multi-language replies',
    text: 'Serve English, Hindi, and Marathi-speaking customers from the same number, the same flows.',
  },
  {
    icon: '📊',
    title: 'One dashboard, everything',
    text: 'Conversations, leads, orders, and automations - all in a single workspace built for your team.',
  },
];

const STEPS = [
  {
    title: 'Connect your number',
    text: 'Link your WhatsApp Business number in minutes via Meta’s official Embedded Signup.',
  },
  {
    title: 'Build your flows',
    text: 'Start from a ready-made template - quotations, ordering, bookings - or design your own.',
  },
  {
    title: 'Let it run',
    text: 'Customers chat, your flows and AI respond, and every reply is logged automatically.',
  },
  {
    title: 'Grow with data',
    text: 'Follow every lead through your pipeline and see what’s working from one dashboard.',
  },
];

/**
 * Public landing page - what an unauthenticated visitor sees at `/`
 * (`ProtectedRoute` renders this instead of redirecting to `/login`, only
 * for the exact root path; every other protected path still redirects as
 * before). WhatsApp-brand palette (`--color-wa-*`, tokens.css), scoped
 * under `.ne-landing` so it never affects the authenticated app's styling.
 */
export function LandingPage() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  usePlatformChat();

  return (
    <div className="ne-landing">
      <header className="ne-landing__header">
        <div className="ne-landing__container">
          <nav className="ne-landing__nav" aria-label="Primary">
            <a className="ne-landing__brand" href="#top">
              <img src={logo} alt="" className="ne-landing__brand-mark" />
              NeuroEdge Connect
            </a>
            <div className="ne-landing__nav-links">
              {NAV_LINKS.map((link) => (
                <a key={link.href} className="ne-landing__nav-link" href={link.href}>
                  {link.label}
                </a>
              ))}
            </div>
            <div className="ne-landing__nav-actions">
              <Button variant="secondary" onClick={() => navigate('/login')}>
                Login
              </Button>
              <Button onClick={() => navigate('/register')}>Get Started</Button>
              <button
                type="button"
                className="ne-landing__nav-toggle"
                aria-expanded={mobileOpen}
                aria-label="Toggle menu"
                onClick={() => setMobileOpen((v) => !v)}
              >
                {mobileOpen ? '✕' : '☰'}
              </button>
            </div>
          </nav>
          <div className={`ne-landing__mobile-panel ${mobileOpen ? 'is-open' : ''}`}>
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                className="ne-landing__nav-link"
                href={link.href}
                onClick={() => setMobileOpen(false)}
              >
                {link.label}
              </a>
            ))}
            <Link className="ne-landing__nav-link" to="/login" onClick={() => setMobileOpen(false)}>
              Login
            </Link>
          </div>
        </div>
      </header>

      <main id="top">
        <section className="ne-landing__hero">
          <div className="ne-landing__container ne-landing__hero-grid">
            <div>
              <span className="ne-landing__eyebrow">WhatsApp Business Automation</span>
              <h1 className="ne-landing__title">
                Turn WhatsApp into your
                <br />
                <span className="ne-landing__title-accent">best sales channel.</span>
              </h1>
              <p className="ne-landing__lede">
                NeuroEdge Connect automates replies, quotations, bookings, and lead capture on
                WhatsApp - so every chat turns into a tracked, winnable deal.
              </p>
              <div className="ne-landing__hero-actions">
                <Button onClick={() => navigate('/register')}>Get Started Free</Button>
                <Button variant="secondary" onClick={() => navigate('/login')}>
                  Login
                </Button>
              </div>
              <p className="ne-landing__hero-note">
                No credit card required to get started. Connect your WhatsApp number in minutes.
              </p>
            </div>

            <div className="ne-landing__phone" aria-hidden="true">
              <div className="ne-landing__phone-screen">
                <div className="ne-landing__phone-bar">
                  <span className="ne-landing__phone-bar-dot" />
                  Your Business
                </div>
                <div className="ne-landing__phone-body">
                  <div className="ne-landing__bubble ne-landing__bubble--in">
                    Hi! Can I get a quote for 5 UPVC pipes?
                  </div>
                  <div className="ne-landing__bubble ne-landing__bubble--out">
                    Sure! Preparing your quotation now 📋
                  </div>
                  <div className="ne-landing__bubble ne-landing__bubble--doc">
                    📄 Quotation_#4821.pdf
                  </div>
                  <div className="ne-landing__bubble ne-landing__bubble--in">
                    Perfect, I'd like to place the order.
                  </div>
                  <div className="ne-landing__bubble ne-landing__bubble--out">
                    Done! Your order ORD-4F2A9C is confirmed. ✅
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="benefits" className="ne-landing__section">
          <div className="ne-landing__container">
            <div className="ne-landing__section-head">
              <h2 className="ne-landing__section-title">Everything your WhatsApp needs</h2>
              <p className="ne-landing__section-lede">
                Built for businesses that live on WhatsApp - from the first reply to the closed
                deal.
              </p>
            </div>
            <div className="ne-landing__grid">
              {BENEFITS.map((b) => (
                <div key={b.title} className="ne-landing__card">
                  <span className="ne-landing__card-icon" aria-hidden="true">
                    {b.icon}
                  </span>
                  <h3 className="ne-landing__card-title">{b.title}</h3>
                  <p className="ne-landing__card-text">{b.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="how-it-works" className="ne-landing__section ne-landing__section--muted">
          <div className="ne-landing__container">
            <div className="ne-landing__section-head">
              <h2 className="ne-landing__section-title">How it works</h2>
              <p className="ne-landing__section-lede">
                Four steps from "connected" to "closing deals on autopilot".
              </p>
            </div>
            <div className="ne-landing__steps">
              {STEPS.map((step, i) => (
                <div key={step.title}>
                  <span className="ne-landing__step-number">{i + 1}</span>
                  <h3 className="ne-landing__card-title">{step.title}</h3>
                  <p className="ne-landing__card-text">{step.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="pricing" className="ne-landing__section">
          <div className="ne-landing__container">
            <div className="ne-landing__section-head">
              <h2 className="ne-landing__section-title">Plans &amp; pricing</h2>
              <p className="ne-landing__section-lede">
                Every new workspace starts with a free trial. Prices are per workspace per month;
                WhatsApp message charges are billed by Meta to your own WhatsApp account.
              </p>
            </div>
            <PlansCatalog
              actionFor={(plan) =>
                plan.isCustomPricing ? (
                  <ContactSales plan={plan} />
                ) : (
                  <Button onClick={() => navigate('/register')}>Start free trial</Button>
                )
              }
            />
          </div>
        </section>

        <section id="about" className="ne-landing__section">
          <div className="ne-landing__container ne-landing__about">
            <div>
              <h2 className="ne-landing__section-title" style={{ textAlign: 'left' }}>
                About NeuroEdge Connect
              </h2>
              <p className="ne-landing__section-lede" style={{ textAlign: 'left', margin: 0 }}>
                NeuroEdge Connect is built by Neuro Edge Technologies LLP to help service businesses
                run their entire customer conversation - replies, quotations, bookings, and
                follow-ups - on the channel their customers already use every day: WhatsApp. We
                built it because running a business shouldn’t mean juggling five different tools
                just to answer one chat.
              </p>
            </div>
            <div className="ne-landing__about-stats">
              <div className="ne-landing__stat">
                <span className="ne-landing__stat-value">24/7</span>
                <span className="ne-landing__stat-label">Automated availability</span>
              </div>
              <div className="ne-landing__stat">
                <span className="ne-landing__stat-value">1</span>
                <span className="ne-landing__stat-label">Dashboard for everything</span>
              </div>
              <div className="ne-landing__stat">
                <span className="ne-landing__stat-value">3+</span>
                <span className="ne-landing__stat-label">Languages supported</span>
              </div>
              <div className="ne-landing__stat">
                <span className="ne-landing__stat-value">Meta</span>
                <span className="ne-landing__stat-label">Official WhatsApp Tech Provider</span>
              </div>
            </div>
          </div>
        </section>

        <section id="contact" className="ne-landing__section ne-landing__section--muted">
          <div className="ne-landing__container">
            <div className="ne-landing__section-head">
              <h2 className="ne-landing__section-title">Contact us</h2>
              <p className="ne-landing__section-lede">
                Questions, a demo, or Enterprise pricing - send us an enquiry and we&apos;ll call
                you back, or call, WhatsApp or email us.
              </p>
              <p className="ne-landing__section-lede">
                <ContactDetails inline />
              </p>
            </div>
            <div className="ne-landing__card" style={{ maxWidth: 560, margin: '0 auto' }}>
              <EnquiryForm />
            </div>
          </div>
        </section>

        <section className="ne-landing__section">
          <div className="ne-landing__container">
            <div className="ne-landing__cta" style={{ maxWidth: 760 }}>
              <h2 className="ne-landing__cta-title">Ready to automate your WhatsApp?</h2>
              <p className="ne-landing__cta-lede">
                Create your workspace and connect your WhatsApp number today.
              </p>
              <Button onClick={() => navigate('/register')}>Get Started Free</Button>
            </div>
          </div>
        </section>
      </main>

      <footer className="ne-landing__footer">
        <div className="ne-landing__container ne-landing__footer-row">
          <span className="ne-landing__footer-copy">
            © {new Date().getFullYear()} Neuro Edge Technologies LLP. All rights reserved.
            <br />
            <ContactDetails inline />
          </span>
          <div className="ne-landing__footer-links">
            <Link to="/pricing">Pricing</Link>
            <Link to="/privacy">Privacy Policy</Link>
            <Link to="/login">Login</Link>
            <Link to="/register">Get Started</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
